/* eslint-disable react/prop-types */
import { useMemo } from "react";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import Button from "@mui/material/Button";
import SoftButton from "components/SoftButton";

// --- HELPERS ---
const createDetail = () => ({
  licenceNumber: "", // A
  licenceDate: "", // B
  registeredStockType: "", // C
  registeredQuantity: "", // 1
  registeredParValue: "", // 2
  documentNumber: "", // D
  documentDate: "", // E
  stockType: "", // G
  actualQuantity: "", // 3
  parValueTotal: "", // 4
  actualIssuePrice: "", // 5
  totalAmount: "", // 6
  unsoldQuantity: "", // 7
  unsoldParValue: "", // 8
});

function StockLedgerInfo({ formData }) {
  const { values = {}, setFieldValue, containView } = formData || {};
  const details = values.details || [createDetail()];

  const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

  // --- LOGIC TÍNH TOÁN ---
  const calculateRow = (it) => {
    const rQty = num(it.registeredQuantity);
    const rPar = num(it.registeredParValue);
    const actQty = num(it.actualQuantity);
    const actPrice = num(it.actualIssuePrice);

    const parValueTotal = actQty > 0 ? actQty * rPar : "";
    const totalAmount = actQty > 0 ? actQty * actPrice : "";
    const unsoldQuantity = (rQty > 0 || actQty > 0) ? rQty - actQty : "";

    return {
      ...it,
      parValueTotal,
      totalAmount,
      unsoldQuantity,
      unsoldParValue: rPar > 0 ? rPar : "",
    };
  };

  const handleHeaderChange = (field, val) => {
    setFieldValue(field, val);
  };

  const handleChangeDetail = (index, field, value) => {
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };

    // Auto Calc cho dòng này
    newDetails[index] = calculateRow(newDetails[index]);
    setFieldValue("details", newDetails);
  };

  const addRow = () => {
    const newList = [...details, createDetail()];
    setFieldValue("details", newList);
  };

  const removeRow = (index) => {
    if (details.length <= 1) return;
    const newList = details.filter((_, i) => i !== index);
    setFieldValue("details", newList);
  };

  // --- TỔNG CỘNG ---
  const totals = useMemo(() => {
    const sum = (field) => details.reduce((acc, it) => acc + num(it[field]), 0);
    return {
      registeredQuantity: sum("registeredQuantity"),
      actualQuantity: sum("actualQuantity"),
      parValueTotal: sum("parValueTotal"),
      totalAmount: sum("totalAmount"),
    };
  }, [details]);

  // --- STYLES ---
  const s = {
    container: { fontFamily: '"Times New Roman", Times, serif', color: "#000", padding: "10px", backgroundColor: "#fff", fontSize: "12px" },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "10px" },
    inputDotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: "12px", fontFamily: "inherit", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    inputDottedSmall: { border: "none", borderBottom: "1px dotted #000", outline: "none", width: "40px", fontSize: "12px", fontFamily: "inherit", textAlign: "center", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    titleBox: { textAlign: "center", margin: "10px 0" },
    titleMain: { fontSize: "16px", fontWeight: "bold", margin: 0 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "11px", marginTop: "10px" },
    th: { border: "1px solid #000", padding: "4px 2px", textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: "30px", verticalAlign: "middle" },
    tdText: { border: "1px solid #000", padding: "0 4px", height: "30px", verticalAlign: "middle", textAlign: "center", fontWeight: "bold" },
    inputTable: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", fontSize: "11px", fontFamily: "inherit", background: "transparent", padding: "0 4px", color: "#000" },
    inputTableCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", fontSize: "11px", fontFamily: "inherit", background: "transparent", color: "#000" },
    totalText: { padding: "0 4px", textAlign: "right", fontWeight: "bold", display: "block" },
    removeBtn: { border: "none", background: "transparent", color: "red", fontWeight: "bold", cursor: "pointer", width: "100%", height: "100%" },
    footerSign: { display: "flex", justifyContent: "space-between", marginTop: "30px", textAlign: "center" },
    signCol: { width: "30%" },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER HAI GÓC */}
      <div style={s.headerTop}>
        <div style={{ width: "45%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị: <input disabled={containView} style={{ ...s.inputDotted, width: "200px" }} value={values.unit || ""} onChange={(e) => handleHeaderChange("unit", e.target.value)} />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Địa chỉ: <input disabled={containView} style={{ ...s.inputDotted, width: "200px" }} value={values.address || ""} onChange={(e) => handleHeaderChange("address", e.target.value)} />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "55%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S43-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "11px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
          </div>
        </div>
      </div>

      {/* TIÊU ĐỀ SỔ CHÍNH GIỮA */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>SỔ CHI TIẾT PHÁT HÀNH CỔ PHIẾU</h2>
        <div style={{ marginTop: 5 }}>
          Năm: <input disabled={containView} style={{ ...s.inputDottedSmall, width: "60px" }} value={values.year || ""} onChange={(e) => handleHeaderChange("year", e.target.value)} />
        </div>
      </div>

      <div style={{ textAlign: "right", marginBottom: 5, fontStyle: "italic", fontSize: "12px" }}>
        Đơn vị tính: <input disabled={containView} style={{ ...s.inputDotted, width: "80px", textAlign: "right" }} value={values.measurementUnit || ""} onChange={(e) => handleHeaderChange("measurementUnit", e.target.value)} />
      </div>

      {/* BẢNG 14 CỘT - 3 TẦNG HEADER */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            {/* TẦNG 1 */}
            <tr>
              <th colSpan={5} style={s.th}>Đăng ký phát hành</th>
              <th colSpan={7} style={s.th}>Phát hành thực tế</th>
              <th colSpan={2} style={s.th}>Số cổ phiếu đã đăng ký phát hành nhưng chưa bán</th>
              {!containView && <th rowSpan={4} style={{ ...s.th, width: "30px" }}>#</th>}
            </tr>
            {/* TẦNG 2 */}
            <tr>
              <th colSpan={2} style={s.th}>Giấy phép phát hành</th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>Loại cổ phiếu</th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>Số lượng</th>
              <th rowSpan={2} style={{ ...s.th, width: "70px" }}>Mệnh giá</th>
              <th colSpan={2} style={s.th}>Chứng từ</th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>Loại cổ phiếu</th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>Số lượng</th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>Giá trị theo mệnh giá</th>
              <th rowSpan={2} style={{ ...s.th, width: "70px" }}>Giá thực tế phát hành</th>
              <th rowSpan={2} style={{ ...s.th, width: "90px" }}>Thành tiền</th>
              <th rowSpan={2} style={{ ...s.th, width: "60px" }}>Số lượng</th>
              <th rowSpan={2} style={{ ...s.th, width: "70px" }}>Mệnh giá</th>
            </tr>
            {/* TẦNG 3 */}
            <tr>
              <th style={{ ...s.th, width: "60px" }}>Số hiệu</th>
              <th style={{ ...s.th, width: "70px" }}>Ngày tháng</th>
              <th style={{ ...s.th, width: "60px" }}>Số hiệu</th>
              <th style={{ ...s.th, width: "70px" }}>Ngày tháng</th>
            </tr>
            {/* ĐÁNH SỐ CỘT */}
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>G</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
            </tr>
          </thead>
          <tbody>
            {details.map((row, i) => (
              <tr key={i}>
                {/* ĐĂNG KÝ (A - 2) */}
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={row.licenceNumber || ""} onChange={(e) => handleChangeDetail(i, "licenceNumber", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={row.licenceDate || ""} onChange={(e) => handleChangeDetail(i, "licenceDate", e.target.value)} placeholder="dd/mm/yyyy" /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={row.registeredStockType || ""} onChange={(e) => handleChangeDetail(i, "registeredStockType", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTable} value={row.registeredQuantity || ""} onChange={(e) => handleChangeDetail(i, "registeredQuantity", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTable} value={row.registeredParValue || ""} onChange={(e) => handleChangeDetail(i, "registeredParValue", e.target.value)} /></td>

                {/* THỰC TẾ (D - 6) */}
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={row.documentNumber || ""} onChange={(e) => handleChangeDetail(i, "documentNumber", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={row.documentDate || ""} onChange={(e) => handleChangeDetail(i, "documentDate", e.target.value)} placeholder="dd/mm/yyyy" /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={row.stockType || ""} onChange={(e) => handleChangeDetail(i, "stockType", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTable} value={row.actualQuantity || ""} onChange={(e) => handleChangeDetail(i, "actualQuantity", e.target.value)} /></td>
                <td style={s.td}><input disabled={true} style={s.inputTable} value={row.parValueTotal || ""} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTable} value={row.actualIssuePrice || ""} onChange={(e) => handleChangeDetail(i, "actualIssuePrice", e.target.value)} /></td>
                <td style={s.td}><input disabled={true} style={s.inputTable} value={row.totalAmount || ""} /></td>

                {/* CHƯA BÁN (7 - 8) */}
                <td style={s.td}><input disabled={true} style={s.inputTable} value={row.unsoldQuantity || ""} /></td>
                <td style={s.td}><input disabled={true} style={s.inputTable} value={row.unsoldParValue || ""} /></td>

                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>X</button>
                  </td>
                )}
              </tr>
            ))}
            {/* DÒNG CỘNG */}
            <tr style={{ backgroundColor: "#fafafa", fontWeight: "bold" }}>
              <td style={s.tdText} colSpan={3}>Cộng</td>
              <td style={s.td}><span style={s.totalText}>{fmt(totals.registeredQuantity)}</span></td>
              <td style={s.tdText}>x</td>
              <td style={s.tdText}>x</td>
              <td style={s.tdText}>x</td>
              <td style={s.tdText}>x</td>
              <td style={s.td}><span style={s.totalText}>{fmt(totals.actualQuantity)}</span></td>
              <td style={s.td}><span style={s.totalText}>{fmt(totals.parValueTotal)}</span></td>
              <td style={s.tdText}>x</td>
              <td style={s.td}><span style={s.totalText}>{fmt(totals.totalAmount)}</span></td>
              <td style={s.tdText}>x</td>
              <td style={s.tdText}>x</td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={3} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              Thêm dòng
            </SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER - SỐ TRANG & NGÀY MỞ SỔ */}
      <div style={{ marginTop: "15px", lineHeight: "1.8", fontSize: "12px" }}>
        <div>- Sổ này có <input disabled={containView} style={s.inputDottedSmall} value={values.totalPages || ""} onChange={(e) => handleHeaderChange("totalPages", e.target.value)} /> trang, đánh số từ trang 01 đến trang...</div>
        <div>- Ngày mở sổ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px" }} value={values.openingDate || ""} onChange={(e) => handleHeaderChange("openingDate", e.target.value)} placeholder="dd/mm/yyyy" /></div>
      </div>

      {/* CHỮ KÝ 3 CỤM */}
      {/* <div style={s.footerSign}>
        <div style={s.signCol}>
          <div style={{ fontWeight: "bold" }}>Người ghi sổ</div>
          <div style={{ fontStyle: "italic" }}>(Ký, họ tên)</div>
          <div style={{ marginTop: "60px", fontWeight: "bold" }}>
             <input disabled={containView} style={{ ...s.inputTableCenter, fontWeight: "bold" }} value={values.ledgerClerk || ""} onChange={(e) => handleHeaderChange("ledgerClerk", e.target.value)} />
          </div>
        </div>
        <div style={s.signCol}>
          <div style={{ fontWeight: "bold" }}>Kế toán trưởng</div>
          <div style={{ fontStyle: "italic" }}>(Ký, họ tên)</div>
          <div style={{ marginTop: "60px", fontWeight: "bold" }}>
             <input disabled={containView} style={{ ...s.inputTableCenter, fontWeight: "bold" }} value={values.chiefAccountant || ""} onChange={(e) => handleHeaderChange("chiefAccountant", e.target.value)} />
          </div>
        </div>
        <div style={s.signCol}>
          <div style={{ fontStyle: "italic", marginBottom: "5px" }}>
            Ngày <input disabled={containView} style={s.inputDottedSmall} value={values.signingDay || ""} onChange={(e) => handleHeaderChange("signingDay", e.target.value)} />
            tháng <input disabled={containView} style={s.inputDottedSmall} value={values.signingMonth || ""} onChange={(e) => handleHeaderChange("signingMonth", e.target.value)} />
            năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "50px" }} value={values.signingYear || ""} onChange={(e) => handleHeaderChange("signingYear", e.target.value)} />
          </div>
          <div style={{ fontWeight: "bold" }}>Người đại diện theo pháp luật</div>
          <div style={{ fontStyle: "italic" }}>(Ký, họ tên, đóng dấu)</div>
          <div style={{ marginTop: "60px", fontWeight: "bold" }}>
             <input disabled={containView} style={{ ...s.inputTableCenter, fontWeight: "bold" }} value={values.legalRepresentative || ""} onChange={(e) => handleHeaderChange("legalRepresentative", e.target.value)} />
          </div>
        </div>
      </div> */}
    </SoftBox>
  );
}

StockLedgerInfo.propTypes = { formData: PropTypes.object };
export { StockLedgerInfo };
